import type { PrintedLanguage } from "../../MathFactsGenerator/PrintedLanguage.ts";

/**
 * Hundredths as a decimal numeral, in integers so nothing is rounded or
 * carries float noise (ADR 0106 §3): 3140 -> "31.4", 628 -> "6.28", 1000 ->
 * "10". No trailing zeros. `mark` is the language's decimal mark - a point for
 * English and Spanish, a comma for French, which is what a French student
 * writes. Written out rather than using toLocaleString so the output cannot
 * vary with the environment's locale, as PlaceValueGenerator's thousands
 * separator is.
 */
export function formatHundredths(hundredths: number, mark: '.' | ','): string {
    const whole = Math.floor(hundredths / 100);
    const fraction = String(hundredths % 100).padStart(2, '0').replace(/0+$/, '');
    return fraction === '' ? String(whole) : `${whole}${mark}${fraction}`;
}

/**
 * Tenths as a decimal numeral with its one decimal always printed (ADR 0125
 * §3): 36 -> "3.6", 120 -> "12.0". A distance asked "to the nearest tenth"
 * keeps its tenth, so a rounded 12.0 is not mistaken for a whole answer.
 */
export function formatTenths(tenths: number, mark: '.' | ','): string {
    return `${Math.floor(tenths / 10)}${mark}${tenths % 10}`;
}

/** The decimal mark a printed language writes (ADR 0106 §3): a comma in French, a point otherwise. */
export function decimalMarkOf(language: PrintedLanguage): '.' | ',' {
    return language === 'fr' ? ',' : '.';
}

/** A no-break space, which is in WinAnsi: how a French number groups its thousands (ADR 0130 §4). */
const NO_BREAK_SPACE = String.fromCharCode(160);

/**
 * A number in standard form, from an integer and the places after its point
 * (ADR 0130 §4): `formatStandard(45200000, 0, 'en')` is "45,200,000" and
 * `formatStandard(72, 5, 'en')` is "0.00072". English and Spanish group thousands
 * with a comma and mark decimals with a point; French groups with a no-break
 * space and marks decimals with a comma ("45 200 000", "0,00072"). The decimals
 * are never grouped. The digits are exactly the integer's, so a trailing zero is
 * printed if the caller leaves one; and nothing here touches a float.
 *
 * `withThousands` (PlaceValueGenerator) is a different helper and stays as it is:
 * it writes a comma in every language, which is right in two of the three.
 */
export function formatStandard(scaled: number, places: number, language: PrintedLanguage): string {
    const digits = String(scaled).padStart(places + 1, '0');
    const whole = digits.slice(0, digits.length - places);
    const fraction = digits.slice(digits.length - places);
    const separator = language === 'fr' ? NO_BREAK_SPACE : ',';
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return places === 0 ? grouped : `${grouped}${decimalMarkOf(language)}${fraction}`;
}

/** A coefficient in hundredths with the language's decimal mark and no trailing zero: 452 -> "4.52", "4,52" in French. */
export function formatCoefficient(hundredths: number, language: PrintedLanguage): string {
    return formatHundredths(hundredths, decimalMarkOf(language));
}
